import { forwardRef, useImperativeHandle } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ExchangeLocationAssignmentsTable } from "./Components/ExchangeLocationAssignmentsTable";
import { ExchangeLocationFormError } from "./Components/ExchangeLocationFormError";
import type {
  ExchangeLocationFormProps,
  ExchangeLocationFormRef,
  ExchangeLocationFormValues,
} from "./types";

export const ExchangeLocationForm = forwardRef<ExchangeLocationFormRef, ExchangeLocationFormProps>(
  (
    {
      isFetching = false,
      errorMessage = "",
      onClickValidate,
      t,
      initialValues = {},
      administrativeAreas,
      exchangeLocations,
      exchangeLocationTypes,
    },
    ref
  ) => {
    const methods = useForm<ExchangeLocationFormValues>({
      defaultValues: initialValues,
      context: t,
    });
    const { control, handleSubmit } = methods;

    useImperativeHandle(ref, () => ({
      submitExchangeLocationsForm: () => {
        void handleSubmit(onClickValidate)();
      },
    }));

    if (isFetching) {
      return (
        <Card className="mb-8 border-neutral-alpha-200 bg-background shadow-none">
          <CardContent className="flex min-h-32 items-center justify-center">
            <Spinner className="size-6 text-primary" />
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="mb-8 border-neutral-alpha-200 bg-background shadow-none">
        <CardContent>
          <FormProvider {...methods}>
            <form className="space-y-6" noValidate onSubmit={handleSubmit(onClickValidate)}>
              <ExchangeLocationFormError message={errorMessage} />
              <Controller
                control={control}
                name="available_exchange_locations"
                render={({ field }) => (
                  <ExchangeLocationAssignmentsTable
                    administrativeAreas={administrativeAreas}
                    exchangeLocations={exchangeLocations}
                    exchangeLocationTypes={exchangeLocationTypes}
                    value={field.value}
                    onChange={field.onChange}
                    t={t}
                  />
                )}
              />
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    );
  }
);

ExchangeLocationForm.displayName = "ExchangeLocationForm";
